import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useEffect } from 'react';

export default function NotFound() {
  // Bulletproof: directly force-set the robots meta to noindex on mount
  // (Helmet doesn't always replace static index.html meta tags reliably)
  useEffect(() => {
    const existing = document.querySelector('meta[name="robots"]');
    const previousContent = existing?.getAttribute('content');
    if (existing) {
      existing.setAttribute('content', 'noindex, nofollow');
    } else {
      const meta = document.createElement('meta');
      meta.setAttribute('name', 'robots');
      meta.setAttribute('content', 'noindex, nofollow');
      document.head.appendChild(meta);
    }
    return () => {
      // Restore original on unmount so other pages stay indexable
      if (existing && previousContent) {
        existing.setAttribute('content', previousContent);
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-cyan-50 flex flex-col">
      {/* SEO: tell search engines NOT to index this page */}
      <Helmet>
        <title>Page Not Found | Minchio Brothers</title>
        <meta name="robots" content="noindex, nofollow" />
        <meta name="description" content="The page you're looking for doesn't exist." />
      </Helmet>

      {/* Top nav */}
      <nav className="absolute top-0 w-full z-10 bg-white/90 backdrop-blur-md shadow-sm">
        <div className="container mx-auto px-6 py-4">
          <Link to="/" className="flex items-center space-x-3 cursor-pointer w-fit">
            <img
              src="https://images.squarespace-cdn.com/content/v1/6329980709c417765c18ef9c/1663928993488-9PHELX1GHR1MGT4SNOLP/minchio+brothers+logo.png?format=100w"
              alt="Minchio Brothers Logo"
              className="h-10 w-10"
            />
            <span className="text-xl font-bold text-gray-900">Minchio Brothers</span>
          </Link>
        </div>
      </nav>

      {/* Body */}
      <main className="flex-1 flex items-center justify-center px-6 pt-24 pb-16">
        <div className="max-w-2xl w-full text-center">
          <p className="text-sm font-semibold tracking-widest text-blue-600 uppercase">404 — Page not found</p>
          <h1 className="mt-4 text-4xl md:text-5xl font-bold text-gray-900 tracking-tight">
            Looks like this page wandered off-set.
          </h1>
          <p className="mt-5 text-lg text-gray-600 leading-relaxed">
            The page you're looking for doesn't exist or has moved. Let's get you back on track —
            you'll find what you need below.
          </p>

          {/* Quick links to real pages */}
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4 text-left">
            <QuickLink to="/" title="Home" description="Our latest work and services overview" />
            <QuickLink to="/real-estate" title="Real Estate" description="Property photography and videography" />
            <QuickLink to="/commercial" title="Commercial" description="Brand storytelling and corporate video" />
            <QuickLink to="/social-media" title="Social Media" description="Content creation for socials" />
            <QuickLink to="/reviews" title="Reviews" description="What our clients say" />
            <QuickLink to="/contact" title="Contact" description="Get in touch for a quote" />
          </div>

          {/* Primary CTA */}
          <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/"
              className="inline-flex items-center justify-center px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-indigo-700 transition shadow-md"
              data-testid="notfound-home-cta"
            >
              Return to home
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center px-6 py-3 border border-gray-300 text-gray-800 rounded-xl font-semibold hover:bg-white transition"
            >
              Contact us
            </Link>
          </div>

          <p className="mt-12 text-sm text-gray-500">
            Need help right away? Call{' '}
            <a href="tel:+61431323137" className="text-blue-600 underline">+61 431 323 137</a>{' '}
            or email{' '}
            <a href="mailto:media@matthewminchio.com" className="text-blue-600 underline">
              media@matthewminchio.com
            </a>
          </p>
        </div>
      </main>
    </div>
  );
}

const QuickLink = ({ to, title, description }) => (
  <Link
    to={to}
    className="group bg-white border border-gray-200 rounded-xl px-5 py-4 hover:border-blue-400 hover:shadow-md transition cursor-pointer"
  >
    <div className="font-semibold text-gray-900 group-hover:text-blue-700">{title}</div>
    <div className="text-sm text-gray-500 mt-1">{description}</div>
  </Link>
);
